import api from "config/axiosConfig";
import { getIssueTitleParts } from "./entityUtils";
import { apiRoutes } from "util/apiRoutes";

/**
 * Minimal issue shape for graph rendering.
 */
export interface GraphIssue {
  id: number;
  name: string;
  cname: string;
  subject_id?: number | null;
  subject_cname?: string | null;
  topic_id?: number | null;
  topic_cname?: string | null;
  properties?: Record<string, unknown>;
  properties_public?: Record<string, unknown>;
}

/**
 * Influence direction for API calls.
 */
export type InfluenceDirection = "influencer" | "influenced";

/**
 * Normalized response for issue influences.
 */
export interface InfluenceListResult {
  issues: GraphIssue[];
  total: number;
}

/**
 * Totals for an issue's influences.
 */
export interface InfluenceTotals {
  influencersTotal: number;
  influencedTotal: number;
}

/**
 * Type guard for plain objects.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Type guard for non-empty strings.
 */
const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

/**
 * Extracts a GraphIssue from a raw API payload.
 */
const parseGraphIssue = (value: unknown): GraphIssue | null => {
  if (!isRecord(value)) return null;
  const id = value.id;
  const cname = value.cname;
  const rawName = value.name;
  const numId = typeof id === "number" ? id : typeof id === "string" ? parseInt(id, 10) : NaN;
  if (!Number.isFinite(numId) || !isNonEmptyString(cname)) return null;

  const name = isNonEmptyString(rawName)
    ? rawName
    : cname.replace(/_/g, " ");

  const subjectCname = isNonEmptyString(value.subject_cname)
    ? value.subject_cname
    : null;
  const subjectId = typeof value.subject_id === "number" ? value.subject_id : null;
  const topicCname = isNonEmptyString(value.topic_cname)
    ? value.topic_cname
    : null;
  const topicId = typeof value.topic_id === "number" ? value.topic_id : null;

  const properties = isRecord(value.properties) ? value.properties : undefined;
  const propertiesPublic = isRecord(value.properties_public)
    ? value.properties_public
    : undefined;

  return {
    id: numId,
    cname,
    name,
    subject_id: subjectId,
    subject_cname: subjectCname,
    topic_id: topicId,
    topic_cname: topicCname,
    properties,
    properties_public: propertiesPublic
  };
};

/**
 * Parses an array of issues from a raw API payload.
 */
const parseGraphIssues = (value: unknown): GraphIssue[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => parseGraphIssue(item))
    .filter((issue): issue is GraphIssue => issue !== null);
};

/**
 * Maps compound influence issues to their base issue (standalone).
 * Preserves properties for EntityAvatar display, matching IssueInfluenceSurface logic.
 */
const mapInfluenceIssue = (
  issue: GraphIssue,
  direction: InfluenceDirection
): GraphIssue => {
  if (direction === "influencer") {
    const id = typeof issue.subject_id === "number" ? issue.subject_id : issue.id;
    const cname = issue.subject_cname || getIssueTitleParts(issue.name).influencer || issue.cname;
    const name = (issue.subject_cname || cname).replace(/_/g, " ");
    const properties = (issue.properties as Record<string, unknown>)?.subject_properties
      ? { subject_properties: (issue.properties as Record<string, unknown>).subject_properties }
      : issue.properties;
    return { id, cname, name, properties };
  }

  const topicId = typeof issue.topic_id === "number" ? issue.topic_id : issue.id;
  const topicCname = issue.topic_cname || getIssueTitleParts(issue.name).influenced || issue.cname;
  const name = (issue.topic_cname || topicCname).replace(/_/g, " ");
  const properties = (issue.properties as Record<string, unknown>)?.topic_properties
    ? { subject_properties: (issue.properties as Record<string, unknown>).topic_properties }
    : issue.properties;
  return { id: topicId, cname: topicCname, name, properties };
};

/**
 * Fetches a single issue by cname.
 */
export const fetchIssueByCname = async (cname: string): Promise<GraphIssue> => {
  if (!isNonEmptyString(cname)) {
    throw new Error("Invalid issue cname.");
  }

  const { data } = await api.get(apiRoutes.issues.byName(encodeURIComponent(cname)));
  const parsed = parseGraphIssue(data);

  if (!parsed) {
    throw new Error("Issue response was invalid.");
  }

  return parsed;
};

/**
 * Fetches influences for a given issue and direction.
 */
export const fetchIssueInfluences = async (
  issueId: number,
  direction: InfluenceDirection,
  limit: number
): Promise<InfluenceListResult> => {
  if (typeof issueId !== "number" || issueId <= 0) {
    throw new Error("Invalid issue id.");
  }

  const safeLimit = Math.max(1, Math.min(100, limit));

  const { data } = await api.get(apiRoutes.issues.influences(issueId), {
    params: {
      type: direction,
      limit: safeLimit,
      offset: 0
    }
  });

  const rawIssues = parseGraphIssues(isRecord(data) ? data.influences : []);
  const mapped = rawIssues.map((issue) => mapInfluenceIssue(issue, direction));
  const uniqueIssues = Array.from(
    mapped.reduce<Map<number, GraphIssue>>((acc, issue) => {
      if (!acc.has(issue.id)) acc.set(issue.id, issue);
      return acc;
    }, new Map())
  ).map(([, issue]) => issue);
  const totalValue = isRecord(data) ? data.total : 0;
  const total = typeof totalValue === "number" ? totalValue : 0;

  return { issues: uniqueIssues, total };
};

/**
 * Response shape for graph roots endpoint.
 */
export interface GraphRootsGroup {
  roots: number[];
  issueIds: number[];
  issues: GraphIssue[];
  edges: { sourceId: number; targetId: number }[];
}

/**
 * Fetches graph root groups (connected components with root issues).
 */
export const fetchGraphRoots = async (): Promise<GraphRootsGroup[]> => {
  const { data } = await api.get<{ groups: GraphRootsGroup[] }>(apiRoutes.graph.roots);
  const groups = data?.groups ?? [];
  return groups.map((g) => ({
    ...g,
    issues: parseGraphIssues(g.issues ?? [])
  }));
};

/**
 * Fetches total counts for an issue's influencers and influenced issues.
 */
export const fetchIssueInfluenceTotals = async (
  issueId: number
): Promise<InfluenceTotals> => {
  const [influencerResult, influencedResult] = await Promise.all([
    fetchIssueInfluences(issueId, "influencer", 100),
    fetchIssueInfluences(issueId, "influenced", 100)
  ]);

  return {
    influencersTotal: influencerResult.issues.length,
    influencedTotal: influencedResult.issues.length
  };
};
